import axios from "axios";
import config from "@/../config";
import { useGeneralStore } from "@/store/General";
import { getDeviceUuid } from "@/core/deviceId";

const apiClient = axios.create({
  baseURL: config.api_base_url_stg,

  headers: {
    Accept: "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    // Generate this per request rather than once at application startup.
    // Some API gateways cache GET requests using the full query string.
    config.params = { ...config.params, time: Date.now() };
    const token = localStorage.getItem("user_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["uuid-device"] = getDeviceUuid();
    }
    config.headers.Lang = localStorage.getItem("lang") || "en";
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.msg || error.response?.data?.message;
    const responseKey = error.response?.data?.key;
    const hadToken = Boolean(localStorage.getItem("user_token"));

    const shouldForceLogout =
      hadToken &&
      (status === 401 ||
        (status === 403 &&
          (responseKey === "single_device" || isDeviceBindingError(message))));

    if (shouldForceLogout) {
      localStorage.removeItem("user_token");
      const logoutMessage =
        status === 401 || !message
          ? "You have been signed out because this account was opened on another device."
          : message;
      useGeneralStore().showSnackbar(logoutMessage, "error");

      if (window.location.pathname !== "/login") {
        import("@/router/index.js").then(({ default: router }) => {
          router.replace({ name: "LoginPage" });
        });
      }

      return Promise.reject(error);
    }

    if (!error.config?.suppressGlobalError && error.response && error.response.data) {
      let g = useGeneralStore();
      g.showSnackbar(`${message || "An unexpected error occurred. Please try again later."}`, "error");
    } else if (!error.config?.suppressGlobalError) {
      let g = useGeneralStore();
      g.showSnackbar(
        "An unexpected error occurred. Please try again later.",
        "error"
      );
    }
    return Promise.reject(error);
  }
);

function isDeviceBindingError(message) {
  if (typeof message !== "string") {
    return false;
  }

  const normalizedMessage = message.toLowerCase();

  return (
    /(device.*(other|another|invalid|not valid|linked|associated|bound)|(account|user).*(linked|associated|bound).*device)/.test(
      normalizedMessage
    ) ||
    /(جهاز.*(آخر|اخر|غير صالح|مرتبط)|الحساب.*مرتبط.*جهاز|معرّ?ف.*جهاز.*غير صالح)/.test(
      message
    )
  );
}

export default apiClient;
